'use client';

import { useState, useEffect } from 'react';

interface DocHealthWidgetProps {
  workspaceId: string;
}

interface WorkspaceHealth {
  avgOverall: number;
  avgCoverage: number;
  avgVerification: number;
  avgFreshness: number;
  totalEntities: number;
  distribution: {
    excellent: number;
    good: number;
    fair: number;
    poor: number;
    critical: number;
  };
}

interface Overview {
  entities_with_docs: number;
  total_docs: number;
  verified_docs: number;
  stale_docs: number;
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-400';
  if (score >= 60) return 'text-blue-400';
  if (score >= 40) return 'text-yellow-400';
  if (score >= 20) return 'text-accent';
  return 'text-red-400';
}

function getScoreLabel(score: number): string {
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Good';
  if (score >= 40) return 'Fair';
  if (score >= 20) return 'Poor';
  return 'Critical';
}

export function DocHealthWidget({ workspaceId }: DocHealthWidgetProps) {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [health, setHealth] = useState<WorkspaceHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHealth = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `/api/secure/command-center/docs/health?workspace_id=${workspaceId}`
        );
        if (!response.ok) {
          throw new Error('Failed to fetch documentation health');
        }
        const data = await response.json();
        setOverview(data.overview);
        setHealth(data.workspaceHealth);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    };

    if (workspaceId) {
      fetchHealth();
    }
  }, [workspaceId]);

  if (loading) {
    return (
      <div className="bg-surface/50 rounded-xl border border-border-muted p-4">
        <div className="flex items-center gap-2 mb-4">
          <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <h3 className="text-sm font-medium text-foreground">Documentation Health</h3>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-8 bg-cream/10 rounded" />
          <div className="h-4 bg-cream/10 rounded w-3/4" />
        </div>
      </div>
    );
  }

  if (error || !health) {
    return (
      <div className="bg-surface/50 rounded-xl border border-border-muted p-4">
        <div className="flex items-center gap-2 mb-4">
          <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <h3 className="text-sm font-medium text-foreground">Documentation Health</h3>
        </div>
        <p className="text-xs text-foreground-tertiary">No documentation data available</p>
      </div>
    );
  }

  const avgScore = health.avgOverall;
  const total = health.totalEntities || 1;

  return (
    <div className="bg-surface/50 rounded-xl border border-border-muted p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <h3 className="text-sm font-medium text-foreground">Documentation Health</h3>
        </div>
        <span className="text-xs text-foreground-tertiary">
          {overview?.total_docs || 0} docs
        </span>
      </div>

      {/* Score */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative w-16 h-16">
          <svg className="w-16 h-16 transform -rotate-90">
            <circle
              cx="32"
              cy="32"
              r="28"
              className="stroke-cream/10"
              strokeWidth="6"
              fill="none"
            />
            <circle
              cx="32"
              cy="32"
              r="28"
              className={getScoreColor(avgScore).replace('text-', 'stroke-')}
              strokeWidth="6"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={`${(avgScore / 100) * 176} 176`}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-lg font-bold ${getScoreColor(avgScore)}`}>
              {avgScore}
            </span>
          </div>
        </div>
        <div>
          <div className={`text-lg font-semibold ${getScoreColor(avgScore)}`}>
            {getScoreLabel(avgScore)}
          </div>
          <div className="text-xs text-foreground-tertiary">
            {overview?.entities_with_docs || 0} of {health.totalEntities} entities
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="space-y-2">
        <div className="flex justify-between text-xs">
          <span className="text-foreground-tertiary">Coverage</span>
          <span className={getScoreColor(health.avgCoverage)}>{health.avgCoverage}%</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-foreground-tertiary">Verified</span>
          <span className={getScoreColor(health.avgVerification)}>{health.avgVerification}%</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-foreground-tertiary">Fresh</span>
          <span className={getScoreColor(health.avgFreshness)}>{health.avgFreshness}%</span>
        </div>
      </div>

      {/* Distribution bar */}
      <div className="mt-4">
        <div className="h-2 rounded-full bg-cream/10 overflow-hidden flex">
          {health.distribution.excellent > 0 && (
            <div
              className="bg-green-400"
              style={{ width: `${(health.distribution.excellent / total) * 100}%` }}
              title={`Excellent: ${health.distribution.excellent}`}
            />
          )}
          {health.distribution.good > 0 && (
            <div
              className="bg-blue-400"
              style={{ width: `${(health.distribution.good / total) * 100}%` }}
              title={`Good: ${health.distribution.good}`}
            />
          )}
          {health.distribution.fair > 0 && (
            <div
              className="bg-yellow-400"
              style={{ width: `${(health.distribution.fair / total) * 100}%` }}
              title={`Fair: ${health.distribution.fair}`}
            />
          )}
          {health.distribution.poor > 0 && (
            <div
              className="bg-accent"
              style={{ width: `${(health.distribution.poor / total) * 100}%` }}
              title={`Poor: ${health.distribution.poor}`}
            />
          )}
          {health.distribution.critical > 0 && (
            <div
              className="bg-red-400"
              style={{ width: `${(health.distribution.critical / total) * 100}%` }}
              title={`Critical: ${health.distribution.critical}`}
            />
          )}
        </div>
        <div className="flex justify-between text-[10px] text-foreground-muted mt-1">
          <span>Critical</span>
          <span>Excellent</span>
        </div>
      </div>
    </div>
  );
}

export default DocHealthWidget;
